import React, { useState, useEffect, useRef } from 'react';
import { Table, Card, DatePicker, Select, Button, message, Tag } from 'antd';
import { SearchOutlined, ReloadOutlined, HistoryOutlined, UserOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import { TransactionActionType, getTransactionActionText, getTransactionActionColor } from '../ticketModels';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ITransaction {
    id: string;
    ticketCode: string;
    ticketTypeName: string;
    studentName: string;
    studentUserInternalId: string;
    actionType: TransactionActionType;
    actionByName: string;
    actionDate: string;
    detail: string;
    note?: string;
}

interface IUser {
    id: string;
    fullName: string;
    userName: string;
}

interface IStudent {
    id: string;
    fullName?: string;
    name?: string;
    userInternalId?: string;
    studentCode?: string;
}

const TransactionLog: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState<ITransaction[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Filters - Mặc định là ngày hôm nay
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);
    const [actionType, setActionType] = useState<TransactionActionType | undefined>(undefined);
    const [userId, setUserId] = useState<string | undefined>(undefined);
    const [studentId, setStudentId] = useState<string | undefined>(undefined);

    // User search
    const [users, setUsers] = useState<IUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Student search
    const [students, setStudents] = useState<IStudent[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const studentDebounceTimer = useRef<any>(null);

    useEffect(() => {
        loadTransactions();
    }, [pageIndex, pageSize]);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async (searchText: string = '') => {
        setLoadingUsers(true);
        try {
            const res: any = await $api.user.getPaging({
                freeTextSearch: searchText || undefined,
                roles: [20, 99],
                pageIndex: 1,
                pageSize: 100
            });

            if (res.result && res.result.items) {
                setUsers(res.result.items);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleUserSearch = (value: string) => {
        if (value && value.length > 0) {
            loadUsers(value);
        }
    };

    const loadTransactions = async () => {
        setLoading(true);
        try {
            const res: any = await $api.ticket.getTransactions({
                pageIndex,
                pageSize,
                startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
                endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
                userId,
                studentId,
                actionType
            });

            if (res.result) {
                setTransactions(res.result.items || []);
                setTotalRecords(res.result.totalRecord || 0);
            }
        } catch (error) {
            message.error('Không thể tải nhật ký giao dịch');
        } finally {
            setLoading(false);
        }
    };

    const searchStudents = async (keyword: string) => {
        if (!keyword || keyword.length < 2) {
            setStudents([]);
            return;
        }

        setLoadingStudents(true);
        try {
            const res: any = await $api.student.getPaging({
                pageIndex: 1,
                pageSize: 20,
                freeTextSearch: keyword
            });
            if (res.result?.items) {
                setStudents(res.result.items);
            }
        } catch (error) {
            console.error('Error searching students:', error);
        } finally {
            setLoadingStudents(false);
        }
    };

    const handleStudentSearch = (value: string) => {
        if (studentDebounceTimer.current) {
            clearTimeout(studentDebounceTimer.current);
        }

        studentDebounceTimer.current = setTimeout(() => {
            if (value && value.length >= 2) {
                searchStudents(value);
            } else {
                setStudents([]);
            }
        }, 500);
    };

    const handleSearch = () => {
        setPageIndex(1);
        loadTransactions();
    };

    const handleReset = () => {
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        setActionType(undefined);
        setUserId(undefined);
        setStudentId(undefined);
        setPageIndex(1);
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange([dates[0], dates[1]]);
        } else {
            setDateRange(null);
        }
    };

    const setTodayRange = () => {
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
    };

    const setYesterdayRange = () => {
        setDateRange([
            dayjs().subtract(1, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day')
        ]);
    };

    const columns = [
        {
            title: <span className="text-base font-bold">Thời gian</span>,
            dataIndex: 'actionDate',
            key: 'actionDate',
            align: 'left',
            width: 160,
            render: (date: string) => (
                <span className="text-sm">
                    {dayjs(date).format('DD/MM/YYYY HH:mm')}
                </span>
            ),
        },
        {
            title: <span className="text-base font-bold">Loại giao dịch</span>,
            dataIndex: 'actionType',
            key: 'actionType',
            width: 140,
            align: 'center' as const,
            render: (type: TransactionActionType) => (
                <Tag color={getTransactionActionColor(type)} className="font-semibold">
                    {getTransactionActionText(type)}
                </Tag>
            ),
        },
        {
            title: <span className="text-base font-bold">Mã phiếu</span>,
            dataIndex: 'ticketCode',
            key: 'ticketCode',
            align: 'center' as const,
            width: 130,
            render: (code: string) => (
                <span className="font-mono font-bold text-blue-600">{code}</span>
            ),
        },
        {
            title: <span className="text-base font-bold">Loại phiếu</span>,
            dataIndex: 'ticketTypeName',
            key: 'ticketTypeName',
            width: 180,
            align: 'left' as const,
        },
        {
            title: <span className="text-base font-bold">Sinh viên</span>,
            dataIndex: 'studentName',
            key: 'studentName',
            width: 200,
            align: 'left' as const,
            render: (name: string, record: ITransaction) => (
                <div>
                    <div className="font-semibold">{name}</div>
                    <div className="text-xs text-gray-500">{record.studentUserInternalId}</div>
                </div>
            ),
        },
        {
            title: <span className="text-base font-bold">Người thực hiện</span>,
            dataIndex: 'actionByName',
            key: 'actionByName',
            width: 160,
            align: 'left' as const,
            render: (name: string) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {name}
                </div>
            ),
        },
        {
            title: <span className="text-base font-bold">Chi tiết</span>,
            dataIndex: 'detail',
            key: 'detail',
            align: 'left' as const,
            render: (detail: string, record: ITransaction) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <div>{detail}</div>
                    {record.note && (
                        <div className="text-xs text-gray-500 italic mt-1">Ghi chú: {record.note}</div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            {/* Filter Section */}
            <Card className="shadow-md">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <HistoryOutlined className="text-blue-500" />
                        Bộ lọc
                    </h3>

                    {/* All Filters on One Line for Desktop */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                        {/* Date Range with Quick Buttons */}
                        <div className="lg:col-span-4">
                            <label className="block text-sm font-medium mb-2">
                                <span className="text-red-500">* </span>Khoảng thời gian
                            </label>
                            <RangePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                format="DD/MM/YYYY"
                                placeholder={['Từ ngày', 'Đến ngày']}
                                className="w-full"
                                size="large"
                            />
                            <div className="flex gap-2 mt-2">
                                <Button
                                    onClick={setTodayRange}
                                    className={`focus:outline-none focus:shadow-none active:shadow-none hover:shadow-none active:scale-100 active:translate-y-0 ${dateRange?.[0]?.isSame(dayjs().startOf('day'), 'day') ? '!border-2 !border-blue-500 !text-blue-600' : ''}`}
                                    style={{ outline: 'none', boxShadow: 'none', transform: 'none' }}
                                >
                                    📅 Hôm nay
                                </Button>
                                <Button
                                    onClick={setYesterdayRange}
                                    className={`focus:outline-none focus:shadow-none active:shadow-none hover:shadow-none active:scale-100 active:translate-y-0 ${dateRange?.[0]?.isSame(dayjs().subtract(1, 'day').startOf('day'), 'day') ? '!border-2 !border-blue-500 !text-blue-600' : ''}`}
                                    style={{ outline: 'none', boxShadow: 'none', transform: 'none' }}
                                >
                                    📆 Hôm qua
                                </Button>
                            </div>
                        </div>

                        {/* Action Type Filter */}
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-medium mb-2">Loại giao dịch</label>
                            <Select
                                placeholder="Tất cả"
                                allowClear
                                value={actionType}
                                onChange={setActionType}
                                className="w-full"
                                size="large"
                            >
                                <Option value={TransactionActionType.Sell}>💰 Bán phiếu</Option>
                                <Option value={TransactionActionType.Use}>✅ Sử dụng phiếu</Option>
                                <Option value={TransactionActionType.Cancel}>❌ Hủy phiếu</Option>
                                <Option value={TransactionActionType.Refund}>💸 Hoàn tiền</Option>
                                <Option value={TransactionActionType.Extend}>⏰ Gia hạn phiếu</Option>
                                <Option value={TransactionActionType.Giveaway}>🎁 Tặng phiếu</Option>
                            </Select>
                        </div>

                        {/* User Filter */}
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-medium mb-2">Người thực hiện</label>
                            <Select
                                showSearch
                                placeholder="Tất cả"
                                allowClear
                                value={userId}
                                onChange={setUserId}
                                onSearch={handleUserSearch}
                                loading={loadingUsers}
                                filterOption={false}
                                className="w-full"
                                size="large"
                            >
                                {users.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        <div className="flex items-center gap-2">
                                            <UserOutlined />
                                            <span>{user.fullName}</span>
                                            <span className="text-xs text-gray-500">({user.userName})</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        {/* Student Filter */}
                        <div className="lg:col-span-4">
                            <label className="block text-sm font-medium mb-2">Sinh viên</label>
                            <Select
                                showSearch
                                placeholder="Nhập tên hoặc mã sinh viên để tìm kiếm"
                                allowClear
                                value={studentId}
                                onChange={setStudentId}
                                onSearch={handleStudentSearch}
                                loading={loadingStudents}
                                filterOption={false}
                                className="w-full"
                                size="large"
                            >
                                {students.map(student => (
                                    <Option key={student.id} value={student.id}>
                                        <div>
                                            <div className="font-semibold">{student.fullName || student.name} | {student.userInternalId}</div>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-start">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            className="focus:outline-none focus:shadow-none active:shadow-none"
                            style={{ outline: 'none' }}
                        >
                            Tìm kiếm
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleReset}
                            className="focus:outline-none focus:shadow-none active:shadow-none"
                            style={{ outline: 'none' }}
                        >
                            Đặt lại
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Transaction Table */}
            <Card className="shadow-md">
                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <HistoryOutlined className="text-blue-500" />
                        Nhật ký giao dịch
                    </h3>
                </div>

                <Table
                    columns={columns}
                    dataSource={transactions}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    size="middle"
                    bordered
                    rowClassName={(record, index) =>
                        index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'
                    }
                    pagination={{
                        current: pageIndex,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        showTotal: (total) => `Tổng ${total} giao dịch`,
                        onChange: (page, size) => {
                            setPageIndex(page);
                            setPageSize(size || 20);
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export default TransactionLog;

